import { ChangeEvent, useRef } from 'react';
import {
  useForm,
  UseFormRegister,
  UseFormReset,
  FormState,
  RegisterOptions,
} from 'react-hook-form';
import { useEmailAnalytics } from 'core/analytics/hooks';
import { normalizeApiError } from 'core/common/errors';
import { isEmailValid } from 'core/common/utils/string';

type EmailFormValues = {
  email: string;
};

type UseEmailFormProps = {
  initialValue: string;
  errorMessage: string;
  onSubmit: (email: string) => Promise<void>;
  onError?: (message: string) => void;
};

type UseEmailFormReturn = {
  email: string;
  register: UseFormRegister<EmailFormValues>;
  registerOptions: RegisterOptions<EmailFormValues, 'email'>;
  formState: FormState<EmailFormValues>;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  handleEmailSelect: (value: string) => void;
  handleEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  handleFormReset: UseFormReset<EmailFormValues>;
};

export const useEmailForm = ({
  initialValue,
  errorMessage,
  onSubmit,
  onError,
}: UseEmailFormProps): UseEmailFormReturn => {
  const { trackFirstEmailChange, trackFirstEmailTouch, trackEmailFilled } = useEmailAnalytics();
  const isFirstChange = useRef(true);

  const { register, handleSubmit, formState, setValue, setError, watch, reset } =
    useForm<EmailFormValues>({
      defaultValues: {
        email: initialValue,
      },
    });

  const email = watch('email');

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isFirstChange.current && e.target.value !== initialValue) {
      trackFirstEmailChange();
      isFirstChange.current = false;
    }
  };

  const handleFocus = () => {
    if (!formState.touchedFields.email && !email) {
      trackFirstEmailTouch();
    }
  };

  const handleFormSubmit = async (data: EmailFormValues) => {
    const submittedEmail = data.email.toLowerCase();

    trackEmailFilled(submittedEmail);

    try {
      await onSubmit(submittedEmail);
    } catch (err) {
      const normalizedError = normalizeApiError(err);
      const message = normalizedError.message || 'Something went wrong. Try again later';
      setError('email', { type: 'server', message });
      onError?.(message);
    }
  };

  const handleEmailSelect = (value: string) => {
    setValue('email', value, { shouldValidate: formState.isSubmitted, shouldTouch: true });
  };

  return {
    email,
    register,
    registerOptions: {
      onChange: handleEmailChange,
      validate: (value: string) => isEmailValid(value) || errorMessage,
    },
    formState,
    handleSubmit: handleSubmit(handleFormSubmit),
    handleEmailSelect,
    handleEmailChange,
    handleFocus,
    handleFormReset: reset,
  };
};
